import React from 'react';
import { Box, Icon, IconProps, Bubble } from '@carta/ink';
import SideNavIcon from '../SideNavIcon/SideNavIcon';
import {
  StyledSideNavItemAnchor,
  StyledSideNavItemLabel,
  StyledSideNavNewTabIcon,
  StyledSideNavBubbleWrapper,
  StyledSideNavItemLink,
} from './styles';
import { SideNavItemProps } from './SideNavItem';

type SideNavItemContentProps = {
  title: string;
  imageIcon?: IconProps['type'];
  openInNewTab?: boolean;
  bubble?: SideNavItemProps['bubble'];
};

const InternalSideNavItemContent = (props: SideNavItemContentProps) => {
  const { title, imageIcon, openInNewTab, bubble } = props;

  return (
    <>
      <Box as="span" display="flex" alignItems="center">
        {imageIcon && <SideNavIcon icon={imageIcon} isBadged={!!bubble} meaning={title} />}
        <StyledSideNavItemLabel>{title}</StyledSideNavItemLabel>
        {bubble && Number(bubble.text) !== 0 && (
          <StyledSideNavBubbleWrapper>
            <Bubble type={bubble.type || 'orange'}>{bubble.text}</Bubble>
          </StyledSideNavBubbleWrapper>
        )}
      </Box>
      {openInNewTab && (
        <StyledSideNavNewTabIcon>
          <Icon type="open-in-new" size="md" />
        </StyledSideNavNewTabIcon>
      )}
    </>
  );
};

type ReactRouterSideNavLinkProps = SideNavItemProps & {
  asGroupItem: boolean;
};

const ReactRouterSideNavLink = React.forwardRef<HTMLAnchorElement, ReactRouterSideNavLinkProps>(
  ({ isActive, isVisible, url, openInNewTab, imageIcon, asGroupItem, ...otherProps }, ref) => (
    <StyledSideNavItemLink
      innerRef={ref}
      isActive={isActive}
      isVisible={isVisible}
      asGroupItem={asGroupItem}
      to={url}
      target={openInNewTab ? '__blank' : undefined}
      rel={openInNewTab ? 'noreferrer noopener' : undefined}
      {...otherProps}
    />
  ),
);

const AnchorSideNavLink = React.forwardRef<HTMLAnchorElement, ReactRouterSideNavLinkProps>(
  ({ url, isActive, isVisible, openInNewTab, asGroupItem, ...otherProps }, ref) => (
    <StyledSideNavItemAnchor
      ref={ref}
      href={url}
      isActive={isActive}
      isVisible={isVisible}
      asGroupItem={asGroupItem}
      target={openInNewTab ? '__blank' : undefined}
      rel={openInNewTab ? 'noreferrer noopener' : undefined}
      {...otherProps}
    />
  ),
);

type SideNavItemContentComponentProps = SideNavItemProps & {
  asGroupItem: boolean;
};

export const SideNavItemContent = React.forwardRef<HTMLAnchorElement, SideNavItemContentComponentProps>(
  (props, ref) => {
    const { isActive = false, withReactRouter, asGroupItem, ...otherProps } = props;

    const SideNavLink = React.memo(withReactRouter ? ReactRouterSideNavLink : AnchorSideNavLink);

    return (
      <Box display="flex">
        <SideNavLink ref={ref} isActive={isActive} asGroupItem={asGroupItem} {...otherProps}>
          <InternalSideNavItemContent
            title={props.title}
            imageIcon={props.imageIcon}
            openInNewTab={props.openInNewTab}
            bubble={props.bubble}
          />
        </SideNavLink>
      </Box>
    );
  },
);
